import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, ChevronRight, Minus, Plus, ShoppingCart, Truck, Store } from 'lucide-react';
import NavBar from '../components/nav';
import { Footer } from '../components/footer';
import { getGroceryShopProductById, getGroceryShopProducts } from '../services/groceryShopApi';
import { useCart } from '../contexts/CartContext';
import namkeFallback from '../assets/namke-fallback.png';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const cardStyle = { background: '#FCFBF5', borderRadius: '24px', border: '1px solid rgba(5,16,4,0.1)', padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '12px' };
const pillStyle = { fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', background: '#FBF9D9', padding: '6px 13px', borderRadius: '999px' };

const ProductFallback = () => (
  <div className="w-full min-h-[504px] flex items-center justify-center bg-namke-cream">
    <img src={namkeFallback} alt="" className="w-full h-full object-cover min-h-[504px]" />
  </div>
);

const ProductImage = ({ src, alt }) => {
  const [error, setError] = useState(false);
  if (!src || error) return <ProductFallback />;
  return (
    <img
      src={src}
      alt={alt || ''}
      className="w-full h-full object-cover min-h-[504px]"
      onError={() => setError(true)}
    />
  );
};

const formatPrice = (p) => {
  const n = typeof p === 'string' ? parseFloat(p) : Number(p);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
};

export default function GroceryShopProductPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getGroceryShopProductById(id);
        if (!cancelled) setProduct(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setError(true);
          setProduct(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!product?.grocery_shop_id) {
      setRelatedProducts([]);
      return;
    }
    let cancelled = false;
    getGroceryShopProducts({ grocery_shop_id: product.grocery_shop_id, active_only: true, limit: 8 })
      .then((list) => {
        if (cancelled) return;
        const filtered = (Array.isArray(list) ? list : []).filter((p) => String(p.id) !== String(product.id));
        setRelatedProducts(filtered.slice(0, 3));
      })
      .catch(() => { if (!cancelled) setRelatedProducts([]); });
    return () => { cancelled = true; };
  }, [product?.grocery_shop_id, product?.id]);

  if (loading) {
    return (
      <>
        <NavBar />
        <div className="min-h-screen bg-namke-white pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-4 animate-pulse">
            <div className="h-4 bg-namke-cream rounded-full w-48 mb-8" />
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-96 h-72 bg-namke-cream rounded-3xl" />
              <div className="flex-1 space-y-4">
                <div className="h-8 bg-namke-cream rounded-full w-3/4" />
                <div className="h-4 bg-namke-cream rounded-full w-full" />
                <div className="h-4 bg-namke-cream rounded-full w-5/6" />
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <NavBar />
        <div className="min-h-screen bg-namke-white pt-24 pb-12 px-4">
          <div className="max-w-xl mx-auto text-center">
            <p className="text-namke-black/70 mb-4">{t('groceryProduct.notFound')}</p>
            <Link to="/" className="font-semibold underline" style={{ color: TERRA }}>
              {t('groceryProduct.backToHome')}
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const origin = [product.origin_country, product.origin_region].filter(Boolean).join(' · ');
  const maxQuantity = product.stock_quantity != null ? product.stock_quantity : null;
  const outOfStock = maxQuantity != null && maxQuantity <= 0;

  const handleAddToCart = () => {
    if (outOfStock) return;
    addToCart({
      id: product.id,
      item_type: 'grocery_product',
      name: product.name,
      price: typeof product.price === 'string' ? parseFloat(product.price) : Number(product.price),
      image: product.image_url,
      restaurant_id: product.restaurant_id,
      restaurantName: product.restaurant_name || product.grocery_shop_name || '',
      quantity,
      supplements: [],
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  return (
    <>
      <NavBar />
      <div className="min-h-screen bg-namke-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-4">
          <nav className="flex items-center gap-2 text-sm text-namke-black/60">
            <Link to="/" className="flex items-center gap-1 hover:text-namke-black transition-colors">
              <Home size={14} />
              {t('groceryProduct.breadcrumbHome')}
            </Link>
            <ChevronRight size={13} className="text-namke-black/30" />
            {product.grocery_shop_id ? (
              <Link to={`/grocery-shop/${product.grocery_shop_id}`} className="hover:text-namke-black transition-colors">
                {product.grocery_shop_name || t('groceryProduct.breadcrumbGrocery')}
              </Link>
            ) : (
              <span>{t('groceryProduct.breadcrumbGrocery')}</span>
            )}
            {product.category ? (
              <>
                <ChevronRight size={13} className="text-namke-black/30" />
                <span>{product.category}</span>
              </>
            ) : null}
            <ChevronRight size={13} className="text-namke-black/30" />
            <span className="text-namke-black font-medium line-clamp-1">{product.name}</span>
          </nav>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))', gap: 'clamp(24px, 4vw, 40px)', alignItems: 'start' }}>
            {/* Image + détails produit */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', minWidth: 0 }}>
              <div style={{ borderRadius: 'clamp(28px, 3vw, 36px)', overflow: 'hidden', boxShadow: '0 12px 30px rgba(5,16,4,0.1)' }}>
                <ProductImage src={product.image_url} alt={product.name} />
              </div>

              {(product.description || origin || product.category || product.unit) && (
                <div style={{ ...cardStyle, marginTop: '4px' }}>
                  <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px', letterSpacing: '-0.02em' }}>{t('groceryProduct.whereFrom')}</h2>
                  {product.description ? (
                    <p style={{ margin: 0, fontSize: '15px', lineHeight: 1.65, color: 'rgba(5,16,4,0.8)', whiteSpace: 'pre-wrap' }}>{product.description}</p>
                  ) : null}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '10px', marginTop: '4px' }}>
                    {origin ? (
                      <span style={{ background: '#FCFBF5', borderRadius: '18px', padding: '13px 15px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>{t('groceryProduct.origin')}</span>
                        <strong style={{ fontSize: '14px' }}>{origin}</strong>
                      </span>
                    ) : null}
                    {product.category ? (
                      <span style={{ background: '#FCFBF5', borderRadius: '18px', padding: '13px 15px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>{t('groceryProduct.category')}</span>
                        <strong style={{ fontSize: '14px' }}>{product.category}</strong>
                      </span>
                    ) : null}
                    {product.unit ? (
                      <span style={{ background: '#FCFBF5', borderRadius: '18px', padding: '13px 15px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>{t('groceryProduct.format')}</span>
                        <strong style={{ fontSize: '14px' }}>{product.unit}</strong>
                      </span>
                    ) : null}
                  </div>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: '14px', marginTop: '4px' }}>
                <div style={cardStyle}>
                  <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>{t('groceryProduct.availability')}</h3>
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                    <span style={{ color: 'rgba(5,16,4,0.62)' }}>{t('groceryProduct.inStock')}</span>
                    <strong>{outOfStock ? t('groceryProduct.soldOut') : product.stock_quantity != null ? `${product.stock_quantity}${product.unit ? ` ${product.unit}` : ''}` : t('groceryProduct.available')}</strong>
                  </span>
                  {origin ? (
                    <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
                      <span style={{ color: 'rgba(5,16,4,0.62)' }}>{t('groceryProduct.origin')}</span>
                      <strong>{origin}</strong>
                    </span>
                  ) : null}
                  <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', gap: '10px' }}>
                    <span style={{ color: 'rgba(5,16,4,0.62)' }}>{t('groceryProduct.soldBy')}</span>
                    <strong style={{ textAlign: 'right' }}>{product.grocery_shop_name || product.restaurant_name || '—'}</strong>
                  </span>
                </div>
                {product.description ? (
                  <div style={cardStyle}>
                    <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '18px' }}>{t('groceryProduct.description')}</h3>
                    <span style={{ fontSize: '14px', lineHeight: 1.6, color: 'rgba(5,16,4,0.78)' }}>{product.description}</span>
                    {product.grocery_shop_id ? (
                      <Link to={`/grocery-shop/${product.grocery_shop_id}`} style={{ fontFamily: 'Archivo, sans-serif', fontSize: '14px', fontWeight: 600, marginTop: '4px', color: PRIMARY }}>
                        {t('groceryProduct.viewFullShop')}
                      </Link>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>

            {/* Colonne info + commande */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', minWidth: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '9px', alignSelf: 'flex-start', background: '#FBF9D9', color: PRIMARY, fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '8px 16px', borderRadius: '999px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: outOfStock ? TERRA : '#72B744' }} />
                  {outOfStock ? t('groceryProduct.outOfStock') : product.stock_quantity != null ? t('groceryProduct.inStockCount', { count: product.stock_quantity, unit: product.unit ? ` ${product.unit}` : '' }) : t('groceryProduct.inStock')}
                </span>
                {product.restaurant_id && product.restaurant_name ? (
                  <p className="text-sm text-namke-black/60">
                    {t('groceryProduct.serviceOfferedBy')}{' '}
                    <Link to={`/restaurant/${product.restaurant_id}`} className="font-semibold hover:underline" style={{ color: PRIMARY }}>
                      {product.restaurant_name}
                    </Link>
                  </p>
                ) : product.grocery_shop_name ? (
                  <p className="text-sm text-namke-black/60">
                    {t('groceryProduct.catalog')} <span className="font-semibold text-namke-black">{product.grocery_shop_name}</span>
                    {product.grocery_shop_description ? (
                      <span className="block text-xs text-namke-black/45 mt-1">{product.grocery_shop_description}</span>
                    ) : null}
                  </p>
                ) : null}
                <div className="flex flex-wrap items-start gap-2">
                  <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(28px, 3.6vw, 40px)', letterSpacing: '-0.02em', lineHeight: 1.05 }}>{product.name}</h1>
                  {product.is_african ? (
                    <span style={{ ...pillStyle, color: PRIMARY, background: 'rgba(5, 70, 37, 0.08)', fontWeight: 700, textTransform: 'uppercase', fontSize: '11px' }}>
                      {t('groceryProduct.africaBadge')}
                    </span>
                  ) : null}
                </div>
                {product.category ? <p className="text-sm text-namke-black/50">{product.category}</p> : null}
              </div>

              <div style={{ ...cardStyle, gap: '22px', boxShadow: '0 10px 26px rgba(5,16,4,0.07)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                  <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '32px', letterSpacing: '-0.02em' }}>{formatPrice(product.price)}</span>
                  {product.unit ? <span className="text-sm text-namke-black/50">/ {product.unit}</span> : null}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>{t('groceryProduct.quantity')}</span>
                  <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', background: '#FBF9D9', borderRadius: '999px', padding: '5px' }}>
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      disabled={outOfStock}
                      style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: '#FCFBF5', color: PRIMARY, display: 'grid', placeItems: 'center', opacity: outOfStock ? 0.4 : 1 }}
                    >
                      <Minus size={13} />
                    </button>
                    <span style={{ minWidth: '26px', textAlign: 'center', fontWeight: 600, fontSize: '16px' }}>{quantity}</span>
                    <button
                      onClick={() => setQuantity((q) => (maxQuantity != null ? Math.min(maxQuantity, q + 1) : q + 1))}
                      disabled={outOfStock || (maxQuantity != null && quantity >= maxQuantity)}
                      style={{ border: 'none', cursor: 'pointer', width: '34px', height: '34px', borderRadius: '999px', background: PRIMARY, color: '#FBF9D9', display: 'grid', placeItems: 'center', opacity: (outOfStock || (maxQuantity != null && quantity >= maxQuantity)) ? 0.4 : 1 }}
                    >
                      <Plus size={13} />
                    </button>
                  </span>
                </div>

                {!outOfStock && product.stock_quantity != null && product.stock_quantity <= 5 ? (
                  <p className="text-sm text-amber-700">{t('groceryProduct.lowStock', { count: product.stock_quantity })}</p>
                ) : null}

                <div style={{ borderTop: '1px solid rgba(5,16,4,0.1)', paddingTop: '18px' }}>
                  <button
                    onClick={handleAddToCart}
                    disabled={outOfStock}
                    style={{
                      width: '100%', border: 'none', cursor: outOfStock ? 'not-allowed' : 'pointer',
                      background: outOfStock ? 'rgba(5,16,4,0.12)' : added ? PRIMARY : TERRA,
                      color: outOfStock ? 'rgba(5,16,4,0.4)' : '#FCFBF5',
                      fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16px', padding: '17px 28px', borderRadius: '999px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    }}
                  >
                    <ShoppingCart size={17} />
                    {outOfStock ? t('groceryProduct.outOfStock') : added ? t('groceryProduct.addedToCart') : t('groceryProduct.addToCart')}
                  </button>
                  {added ? (
                    <p style={{ margin: '10px 0 0', fontFamily: 'Archivo, sans-serif', fontSize: '13px', textAlign: 'center', color: 'rgba(5,16,4,0.62)' }}>
                      {t('groceryProduct.addedDeliveredNote')}
                    </p>
                  ) : null}
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(5,16,4,0.74)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FBF9D9', padding: '12px 18px', borderRadius: '20px' }}>
                  <Truck size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
                  {t('groceryProduct.deliveredWithOrder')}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '10px', background: '#FBF9D9', padding: '12px 18px', borderRadius: '20px' }}>
                  <Store size={16} style={{ color: PRIMARY, flexShrink: 0 }} />
                  {t('groceryProduct.soldByName', { name: product.grocery_shop_name || product.restaurant_name || 'namke' })}
                </span>
              </div>
            </div>
          </div>

          {relatedProducts.length > 0 && (
            <section style={{ marginTop: 'clamp(40px, 5vw, 64px)', display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(22px, 2.6vw, 30px)', letterSpacing: '-0.02em' }}>{t('groceryProduct.oftenBoughtWith')}</h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 200px), 1fr))', gap: '16px' }}>
                {relatedProducts.map((rp) => (
                  <Link
                    key={rp.id}
                    to={`/grocery-shop-product/${rp.id}`}
                    style={{ background: '#FCFBF5', border: '1px solid rgba(5,16,4,0.1)', borderRadius: '22px', overflow: 'hidden', display: 'flex', flexDirection: 'column', color: '#051004' }}
                  >
                    <div style={{ width: '100%', aspectRatio: '1', background: '#FBF9D9', overflow: 'hidden' }}>
                      {rp.image_url ? (
                        <img src={rp.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.src = namkeFallback; }} />
                      ) : (
                        <img src={namkeFallback} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      )}
                    </div>
                    <span style={{ padding: '15px 17px 17px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }}>{rp.name}</strong>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.64)' }}>
                        {[rp.unit, rp.origin_country].filter(Boolean).join(' · ')}
                      </span>
                      <span style={{ fontWeight: 600, fontSize: '16px' }}>{formatPrice(rp.price)}</span>
                    </span>
                  </Link>
                ))}
                {product.grocery_shop_id ? (
                  <Link
                    to={`/grocery-shop/${product.grocery_shop_id}`}
                    style={{ background: PRIMARY, color: '#FBF9D9', borderRadius: '22px', padding: '22px', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}
                  >
                    <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '20px', lineHeight: 1.15 }}>{product.grocery_shop_name || t('groceryProduct.viewShop')}</strong>
                    <span style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(251,249,217,0.82)' }}>{t('groceryProduct.discoverCatalog')}</span>
                    <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', fontWeight: 600, color: '#FFC717', marginTop: '4px' }}>{t('groceryProduct.seeAll')}</span>
                  </Link>
                ) : null}
              </div>
            </section>
          )}

          <div className="mt-8">
            <Link
              to={product.grocery_shop_id ? `/grocery-shop/${product.grocery_shop_id}` : '/'}
              className="inline-flex items-center text-sm font-semibold hover:underline"
              style={{ color: PRIMARY }}
            >
              {t('groceryProduct.backToShop')}{product.grocery_shop_name ? ` · ${product.grocery_shop_name}` : ''}
            </Link>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
